import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { CalendarClock, Loader2, Plus, Clock, CheckCircle2, XCircle } from "lucide-react";
import { timeAgo } from "@/lib/format";

/**
 * My events — the current user's submissions (any status).
 * Backend: `GET /api/classifieds/events/mine` → `{events: [...]}`.
 * Approved events also appear on the public /events list; pending &
 * rejected events are only visible here.
 */
export default function MyEventsPage() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: ["my-events", user?.id],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/events/mine");
      return data.events || [];
    },
    enabled: !!user,
    staleTime: 30_000,
  });

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="my-events-page">
      <Seo title="My events" />
      <Breadcrumbs items={[{ label: "My events" }]} />

      <div className="mt-6 flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900">My events</h1>
          <p className="mt-2 text-slate-500">Track submissions, see approval status, and post new events.</p>
        </div>
        <Link
          to="/events/post"
          data-testid="my-events-post-cta"
          className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 shadow-card"
        >
          <Plus className="w-4 h-4" /> Post your event
        </Link>
      </div>

      {q.isLoading && (
        <div className="mt-16 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      )}

      {!q.isLoading && q.isError && (
        <div className="mt-12 rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
          Couldn&apos;t load your events.{" "}
          <button onClick={() => q.refetch()} className="ml-1 font-bold underline">
            Retry
          </button>
        </div>
      )}

      {!q.isLoading && !q.isError && (q.data || []).length === 0 && (
        <div className="mt-12 rounded-2xl border border-dashed border-soft bg-white p-12 text-center">
          <CalendarClock className="w-10 h-10 mx-auto text-slate-300" />
          <div className="mt-3 font-display font-bold text-slate-900 text-lg">
            No events yet
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Submit your first event — reviews usually happen within 24 hours.
          </p>
          <Link
            to="/events/post"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-bold px-5 py-2.5"
          >
            <Plus className="w-4 h-4" /> Post your event
          </Link>
        </div>
      )}

      <div className="mt-8 space-y-4">
        {(q.data || []).map((e) => (
          <MyEventRow key={e.id} event={e} />
        ))}
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const norm = (status || "").toLowerCase();
  const map = {
    pending: {
      cls: "bg-amber-50 text-amber-700 border-amber-200",
      Icon: Clock,
      label: "Pending review",
    },
    approved: {
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
      Icon: CheckCircle2,
      label: "Approved · Live",
    },
    rejected: {
      cls: "bg-red-50 text-red-700 border-red-200",
      Icon: XCircle,
      label: "Rejected",
    },
  };
  const it = map[norm] || {
    cls: "bg-slate-50 text-slate-700 border-slate-200",
    Icon: Clock,
    label: status || "Unknown",
  };
  const Icon = it.Icon;
  return (
    <span
      data-testid={`my-event-status-${norm}`}
      className={`inline-flex items-center gap-1 rounded-full border ${it.cls} px-2.5 py-1 text-[11px] font-bold`}
    >
      <Icon className="w-3.5 h-3.5" /> {it.label}
    </span>
  );
}

function MyEventRow({ event: e }) {
  const status = (e.status || "").toLowerCase();
  const isApproved = status === "approved";
  return (
    <div
      data-testid={`my-event-row-${e.id}`}
      className="rounded-2xl bg-white shadow-card overflow-hidden flex flex-col md:flex-row"
    >
      {e.image_url && (
        <div className="md:w-56 shrink-0 bg-slate-100">
          <img src={e.image_url} alt={e.title} className="w-full h-40 md:h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="p-5 flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display font-bold text-lg text-slate-900 truncate">{e.title}</h3>
          <StatusChip status={e.status} />
        </div>
        <div className="mt-1 text-xs text-slate-500">
          Submitted {timeAgo(e.created_at)}
        </div>
        {e.venue && <div className="mt-2 text-sm text-slate-600 truncate">{e.venue}</div>}
        {status === "rejected" && e.reject_reason && (
          <div
            data-testid={`my-event-reject-reason-${e.id}`}
            className="mt-3 rounded-lg bg-red-50 border border-red-100 p-3 text-sm text-red-800"
          >
            <span className="font-bold">Rejection reason: </span>
            {e.reject_reason}
          </div>
        )}
        <div className="mt-4 flex items-center gap-3">
          {isApproved && (
            <Link
              to={`/events/${e.id}`}
              data-testid={`my-event-view-${e.id}`}
              className="rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-sm font-bold px-4 py-2 transition-colors"
            >
              View live event
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
